import React from 'react';
import size from 'lodash/size';

import { I18nText, IsDisabled } from '../../../../../../../types';
import { ThreeDStockContentSortDropdownButtonButtonsType } from './ThreeDStockContentSortDropdownButton.types';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { useDropdownState } from '../../../../../../../helpers/dropdowns/hooks/useDropdownState';

import { PureButtonHelper } from '../../../../../../../helpers/buttons/PureButtonHelper';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';
import { RefButtonHelper } from '../../../../../../../helpers/buttons/RefButtonHelper';
import { Popover } from '../../../../../../../helpers/Popover';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';

import { PopoverPlacement } from '../../../../../../../helpers/Popover/popoverConstants';

import { words } from '../../../../../../../locales/keys';

const dropdownOffset = 10;

interface ThreeDStockContentSortDropdownButtonProps {
  currentSortI18nText: I18nText;
  disabled?: IsDisabled;
  buttons: ThreeDStockContentSortDropdownButtonButtonsType[];
}

function ThreeDStockContentSortDropdownButton({
  currentSortI18nText,
  disabled,
  buttons
}: ThreeDStockContentSortDropdownButtonProps) {
  const {
    isPopoverOpen,
    referencePopoverElement,
    referencePopoverCallbackRef,
    togglePopover,
    closePopover
  } = useDropdownState();

  if (size(buttons) === 0) {
    return null;
  }

  return (
    <div className="relative inline-flex">
      <RefButtonHelper
        dataGa="sort-dropdown-button"
        className="py-2.5 pl-2.5 pr-2.5 flex-row-reverse rounded-md inline-flex items-center leading-5 focus:ring-base border border-gray-300 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 ease-out"
        onClick={togglePopover}
        ref={referencePopoverCallbackRef}
        disabled={disabled}
      >
        <Icon icon={IconsEnum.CHEVRON_DOWN_SOLID} className="h-5 w-5" />

        <div className="flex items-center gap-1 text-sm pl-2 pr-1">
          <span className="text-gray-500 dark:text-gray-400">
            <Translate id={words.sortBy} />:
          </span>
          <span className="font-semibold">
            <Translate id={currentSortI18nText} />
          </span>
        </div>
      </RefButtonHelper>

      {isPopoverOpen ? (
        <Popover
          className="min-w-48 z-30 overflow-y-auto bg-white border border-transparent dark:bg-gray-700 dark:border-opacity-10 dark:border-white dark:text-white focus:outline-none py-1 ring-1 ring-black ring-opacity-5 rounded-md shadow-lg text-gray-700"
          closePopover={closePopover}
          distanceOffset={dropdownOffset}
          placement={PopoverPlacement.BOTTOM_START}
          referenceElement={referencePopoverElement}
        >
          {buttons.map((button) =>
            button.active ? (
              <PureIconButtonHelper
                key={button.id}
                className="flex flex-row-reverse text-left dark:hover:bg-gray-800 hover:bg-gray-100 px-4 py-2 text-sm w-full whitespace-nowrap"
                dataGa={button.id}
                i18nText={button.i18nText}
                onClick={button.onSelect}
                icon={IconsEnum.CHECK}
                iconClassName="text-blue-600 h-5 w-5"
                i18nTextClassName="flex items-center text-md sm:text-xs 3xl:text-sm w-full whitespace-nowrap pr-1.5"
              />
            ) : (
              <PureButtonHelper
                key={button.id}
                className="flex text-left dark:hover:bg-gray-800 hover:bg-gray-100 px-4 py-2 text-sm w-full whitespace-nowrap"
                i18nTextClassName="flex items-center text-md sm:text-xs 3xl:text-sm w-full whitespace-nowrap"
                dataGa={button.id}
                i18nText={button.i18nText}
                onClick={button.onSelect}
              />
            )
          )}
        </Popover>
      ) : null}
    </div>
  );
}

export default ThreeDStockContentSortDropdownButton;
